/**
 * Zoom OAuth state for the dashboard (Connect to Zoom / stored credentials from DB).
 * Reads from API endpoint that queries the database.
 */

import React, { createContext, useContext, useCallback, useState, useEffect } from 'react'
import { zoomAPI, getConnectZoomUrl } from '../services/api'
import { useAuth } from './AuthContext'

const ZoomOAuthContext = createContext(null)

export function ZoomOAuthProvider({ children }) {
  const { isAuthenticated, user } = useAuth()
  const [stored, setStored] = useState(null)
  const [loading, setLoading] = useState(false)

  const fetchCredentials = useCallback(async () => {
    // Only fetch if user is authenticated
    if (!isAuthenticated) {
      setStored(null)
      setLoading(false)
      return false
    }

    setLoading(true)
    try {
      const res = await zoomAPI.getOAuthCredentials()
      const data = res?.data
      if (data && data.connected) {
        setStored({
          email: data.email,
        })
        return true
      }
      setStored(null)
      return false
    } catch {
      // not connected or API error
      setStored(null)
      return false
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  const clearCredentials = useCallback(() => {
    setStored(null)
  }, [])

  // Fetch credentials when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchCredentials()
    } else {
      // Clear stored credentials when user logs out
      setStored(null)
      setLoading(false)
    }
  }, [isAuthenticated, fetchCredentials])

  // After OAuth callback we land with ?zoom_connected=1 – fetch credentials
  useEffect(() => {
    if (!isAuthenticated) return

    const params = new URLSearchParams(window.location.search)
    if (params.get('zoom_connected') === '1') {
      params.delete('zoom_connected')
      const newSearch = params.toString()
      const path = window.location.pathname === '/' ? '/dashboard' : window.location.pathname
      const newUrl = path + (newSearch ? `?${newSearch}` : '') + window.location.hash
      window.history.replaceState(null, '', newUrl)
      fetchCredentials()
    }
  }, [isAuthenticated, fetchCredentials])

  const value = {
    isConnected: !!stored,
    email: stored?.email ?? null,
    connectZoomUrl: getConnectZoomUrl(user?.id), // Returns null if not authenticated
    fetchCredentials,
    clearCredentials,
    loading,
  }

  return (
    <ZoomOAuthContext.Provider value={value}>
      {children}
    </ZoomOAuthContext.Provider>
  )
}

export function useZoomOAuth() {
  const ctx = useContext(ZoomOAuthContext)
  if (!ctx) throw new Error('useZoomOAuth must be used within ZoomOAuthProvider')
  return ctx
}
